import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { StockAlert, StockAlertResolutionRequest } from '../../../models/StockAlert';
import { StockAlertsService, StockAlertPagedResult } from '../../../services/stock-alerts.service';
import { PaginationOptions } from '../../../shared/interfaces/pagination.interface';
import { StockService } from '../../../services/stock.service';
import { WarehousesService } from '../../../services/warehouses.service';
import { StatusService } from '../../../services/status.service';
import { Status } from '../../../models/Status';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { Stock } from '../../../models/Stock';
import { Warehouse } from '../../../models/Warehouse';

@Component({
  selector: 'app-stock-alerts',
  templateUrl: './stock-alerts.component.html',
  styleUrls: ['./stock-alerts.component.css']
})
export class StockAlertsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ===== DADOS =====
  stockAlerts: StockAlert[] = [];
  filteredData: StockAlert[] = [];
  stocks: Stock[] = [];
  warehouses: Warehouse[] = [];
  isLoading = false;
  error: string | null = null;

  // ===== PAGINAÇÃO =====
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // ===== FILTROS =====
  filters = {
    alertType: '',
    status: '',
    stockId: '',
    warehouseId: ''
  };

  statuses: Status[] = [];

  alertTypes = [
    { value: 'LowStock', label: 'Estoque Baixo' },
    { value: 'HighStock', label: 'Estoque Alto' },
    { value: 'Expired', label: 'Expirado' },
    { value: 'NearExpiration', label: 'Próximo da Expiração' },
    { value: 'Damaged', label: 'Danificado' },
    { value: 'QualityIssue', label: 'Problema de Qualidade' }
  ];

  // ===== CONFIGURAÇÃO DA TABELA =====
  tableTitle = 'Alertas de Estoque';
  showSearch = true;
  searchPlaceholder = 'Buscar por item, mensagem, tipo...';
  clickable = true;

  columns = [
    { titulo: 'ID', label: 'id' },
    { titulo: 'Item', label: 'itemName' },
    { titulo: 'Tipo', label: 'alertType' },
    { titulo: 'Mensagem', label: 'message' },
    { titulo: 'Data', label: 'alertDate' },
    { titulo: 'Status', label: 'resolved' }
  ];

  actions: TableAction[] = [
    {
      label: 'Resolver',
      icon: 'fas fa-circle-check',
      action: 'resolve',
      class: 'btn-success'
    },
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    }
  ];

  constructor(
    private stockAlertsService: StockAlertsService,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private errorService: ErrorService,
    private statusService: StatusService
  ) {}

  ngOnInit(): void {
    this.loadStatuses();
    this.loadStocks();
    this.loadWarehouses();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStocks(): void {
    this.stockService.getAll({ page: 1, pageSize: 1000 }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.stocks = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar estoques:', error);
      }
    });
  }

  loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar almoxarifados:', error);
      }
    });
  }

  loadStatuses(): void {
    this.statusService.getByEntity('StockAlert').subscribe({
      next: (statuses: Status[]) => {
        this.statuses = statuses;
      },
      error: (error: any) => {
        console.error('Erro ao carregar status para StockAlert:', error);
        this.statuses = [];
      }
    });
  }

  loadData(): void {
    this.isLoading = true;
    this.error = null;
    const options: PaginationOptions = { page: this.currentPage, pageSize: this.pageSize };

    this.stockAlertsService.getUnresolved(options).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (result: StockAlertPagedResult) => {
        this.stockAlerts = result.items || [];
        this.totalItems = result.totalItems;
        this.totalPages = result.totalPages;
        this.hasNextPage = this.currentPage < this.totalPages;
        this.hasPreviousPage = this.currentPage > 1;
        this.currentPage = result.page;
        this.pageSize = result.pageSize;
        this.applyFilters();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar alertas:', error);
        this.error = 'Erro ao carregar alertas. Tente novamente.';
        this.isLoading = false;
        this.stockAlerts = [];
        this.filteredData = [];
      }
    });
  }

  applyFilters(): void {
    let filtered = [...this.stockAlerts];

    if (this.filters.alertType) {
      filtered = filtered.filter(alert => alert.alertType === this.filters.alertType);
    }

    if (this.filters.status) {
      const statusName = this.filters.status.toLowerCase();
      // Mapeamento temporário de status dinâmico para booleano resolved
      // até que o backend suporte filtragem completa por status
      const isResolved = statusName.includes('resolvido') || statusName === 'resolved' || statusName === 'concluído' || statusName === 'completed';
      filtered = filtered.filter(alert => alert.resolved === isResolved);
    }

    // Filtro de estoque - por enquanto não implementado pois StockAlert não tem referência direta ao estoque
    // if (this.filters.stockId) {
    //   const stockId = Number(this.filters.stockId);
    //   filtered = filtered.filter(alert => {
    //     // Assumindo que o alerta tem uma referência ao estoque através do item
    //     // Isso pode precisar ser ajustado dependendo do modelo de dados
    //     return true; // Temporário - precisa verificar como o estoque está relacionado
    //   });
    // }

    if (this.filters.warehouseId) {
      const warehouseId = Number(this.filters.warehouseId);
      filtered = filtered.filter(alert => {
        // Assumindo que o alerta tem uma referência ao almoxarifado através do item
        // Isso pode precisar ser ajustado dependendo do modelo de dados
        return true; // Temporário - precisa verificar como o almoxarifado está relacionado
      });
    }

    this.filteredData = filtered;
  }

  clearFilters(): void {
    this.filters = {
      alertType: '',
      status: '',
      stockId: '',
      warehouseId: ''
    };
    this.applyFilters();
  }

  onActionClick(event: { action: string, item: any }): void {
    const { action, item } = event;

    switch (action) {
      case 'resolve':
        this.resolveAlert(item);
        break;
      case 'view':
        this.viewAlert(item);
        break;
    }
  }

  resolveAlert(alert: StockAlert): void {
    if (!alert.id) return;

    const resolutionRequest: StockAlertResolutionRequest = {
      resolutionDate: new Date().toISOString(),
      resolutionNotes: 'Resolvido via sistema de alertas'
    };

    this.stockAlertsService.resolveAlert(alert.id, resolutionRequest).subscribe({
      next: () => {
        this.loadData();
      },
      error: (error) => {
        console.error('Erro ao resolver alerta:', error);
        this.error = 'Erro ao resolver alerta.';
      }
    });
  }

  viewAlert(alert: StockAlert): void {
    // TODO: Implementar visualização detalhada do alerta
  }

  refreshAlerts(): void {
    this.loadData();
  }

  retryLoad(): void {
    this.loadData();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadData();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadData();
  }

  // ===== HELPERS =====
  getAlertTypeLabel(type: string): string {
    const alertType = this.alertTypes.find(at => at.value === type);
    return alertType ? alertType.label : type;
  }

  isAlertUrgent(alert: StockAlert): boolean {
    return alert.alertType === 'Expired' ||
           alert.alertType === 'Damaged' ||
           alert.alertType === 'QualityIssue';
  }

  getUnresolvedCount(): number {
    return this.stockAlerts.filter(alert => !alert.resolved).length;
  }

  getResolvedCount(): number {
    return this.stockAlerts.filter(alert => alert.resolved).length;
  }

  getUrgentCount(): number {
    return this.stockAlerts.filter(alert => this.isAlertUrgent(alert) && !alert.resolved).length;
  }
}
